import React, { useState, useEffect } from 'react';

import {
  CheckCircleOutlined,
  InboxOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Modal as ModalAntD,
  Row,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Upload,
  message,
} from 'antd';

// antd 4.10's ModalProps type does not declare `children`; project-wide pattern
// (see FinancialTable: `const Drawer: any = DrawerAntD`) is to re-type as `any`.
const Modal: any = ModalAntD;
import type { RcFile, UploadFile } from 'antd/lib/upload/interface';
import moment, { Moment } from 'moment';
import styled from 'styled-components';
import * as XLSX from 'xlsx';

import {
  RECONCILIATION_STATUS_META,
  ReconciliationDetail,
  ReconciliationUploadResponse,
  ReconciliationWarning,
  downloadReconciliationReport,
  getReconciliationView,
  reprocessReconciliation,
  reprocessReconciliationBatch,
  uploadReconciliation,
} from '../../../hooks/reconciliation';
import ReconciliationDetailModal from '../ReconciliationDetailModal';
import ReconciliationManualModal, {
  ManualMode,
  ManualTargetRow,
} from '../ReconciliationManualModal';

const Dragger: any = Upload.Dragger;

const MAX_SIZE_MB = 10;

const SectionTitle = styled.div`
  font-weight: 600;
  margin-bottom: 8px;
  margin-top: 16px;
`;

/**
 * MV2-1909: link "Como obter esse arquivo no PagBank?" ao lado dos títulos de upload.
 * Passos conforme tutorial oficial (public/tutorial-relatorios-pagbank.pdf). O popover traz
 * o passo-a-passo resumido + botão pra abrir o PDF completo com screenshots.
 */
const TUTORIAL_PDF_URL = '/tutorial-relatorios-pagbank.pdf';

const AlertBox = styled.div`
  background: #fff2f0;
  border: 1px solid #ffccc7;
  border-radius: 4px;
  padding: 8px 12px;
  margin-top: 12px;
  font-size: 12px;
  color: #a8071a;
`;

const HelpLink: React.FC<{ type: 'extrato' | 'vendas' }> = ({ type }) => {
  const [open, setOpen] = useState(false);
  const content = type === 'extrato' ? (
    <div style={{ maxWidth: 360 }}>
      <strong>Extrato da Conta</strong>
      <ol style={{ paddingLeft: 16, margin: '8px 0 0' }}>
        <li>Acesse <em>minhaconta.pagbank.com.br</em> e, no menu lateral, vá em <strong>Conta PagBank &gt; Extratos da Conta</strong>.</li>
        <li>No campo <strong>Período</strong>, selecione <strong>Personalizado</strong>.</li>
        <li>Em <strong>Selecionar dias</strong>, escolha o primeiro e o último dia do mês (ex: 01/09/2026 – 30/09/2026).</li>
        <li>Clique em <strong>Buscar</strong> para carregar os dados.</li>
        <li>No canto superior direito da tabela, clique em <strong>Exportar extrato</strong>.</li>
        <li>Em <strong>Formato do arquivo</strong>, selecione <strong>.csv (Separado por vírgulas)</strong>.</li>
        <li>Clique em <strong>Exportar</strong>. O arquivo é baixado automaticamente.</li>
      </ol>
      <AlertBox>
        Sempre selecione <strong>mês completo</strong> (dia 1 ao último dia do mês) e formato <strong>CSV</strong>.
        Não use PDF, XLSX ou OFX.
      </AlertBox>
    </div>
  ) : (
    <div style={{ maxWidth: 360 }}>
      <strong>Vendas e Recebimentos</strong>
      <ol style={{ paddingLeft: 16, margin: '8px 0 0' }}>
        <li>Acesse <em>minhaconta.pagbank.com.br</em> e, no menu lateral, vá em <strong>Meu Negócio &gt; Extratos e Relatórios</strong>.</li>
        <li>Clique no <strong>ícone de filtro</strong> (barras) ao lado do ícone de download.</li>
        <li>No painel lateral, selecione <strong>Personalizado</strong> e escolha o mês completo (ex: 01/09/2026 – 30/09/2026).</li>
        <li>Clique em <strong>Aplicar</strong>.</li>
        <li>Confira que a barra mostra o período correto e clique no <strong>ícone de download</strong> (seta pra baixo).</li>
        <li>Em <strong>Escolha o formato do arquivo</strong>, selecione <strong>CSV</strong> (opção &quot;Para planilhas e sistemas&quot;).</li>
        <li>Clique em <strong>Exportar</strong>. O arquivo é baixado automaticamente.</li>
      </ol>
      <AlertBox>
        Sempre selecione <strong>mês completo</strong> e formato <strong>CSV</strong>.
        Não use PDF, TXT ou XML.
      </AlertBox>
    </div>
  );
  const Modal2: any = ModalAntD;
  return (
    <>
      <a
        style={{ fontSize: 12, fontWeight: 400 }}
        onClick={(e) => { e.preventDefault(); setOpen(true); }}
      >
        Como obter esse arquivo no PagBank?
      </a>
      <Modal2
        visible={open}
        onCancel={() => setOpen(false)}
        onOk={() => setOpen(false)}
        okText='Entendi'
        cancelButtonProps={{ style: { display: 'none' } }}
        title='Como obter esse arquivo no PagBank'
        width={460}
      >
        {content}
        <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
          <a href={TUTORIAL_PDF_URL} target='_blank' rel='noopener noreferrer'>
            Ver tutorial completo com screenshots (PDF)
          </a>
        </div>
      </Modal2>
    </>
  );
};

interface Props {
  visible: boolean;
  onClose: () => void;
  onDone?: () => void;
  /**
   * MV2-1909: quando setado, modal entra em "modo view":
   *  - não mostra o fluxo de upload (sem drag-drop, sem botão processar);
   *  - carrega o estado atual da competência via GET /reconciliations/view;
   *  - mostra o mesmo resumo + lista de divergências com ações.
   * Usado pela tela Histórico quando o operador clica "Ver detalhes".
   */
  viewPeriod?: string | null;
}

const getExt = (file: RcFile): string => {
  const name = (file.name || '').toLowerCase();
  const idx = name.lastIndexOf('.');
  return idx >= 0 ? name.slice(idx) : '';
};

const isAcceptedFormat = (file: RcFile): boolean => {
  const ext = getExt(file);
  return ext === '.csv' || ext === '.xlsx';
};

// MV2-1909: linha da tabela de divergências (1 por tx, consolidando warnings da mesma tx).
interface DivergenceRow {
  key: string;
  transactionPk: number;
  valueBrl: number | null;
  motivo: React.ReactNode;
  motivoText: string; // pro accessibility/copy
  ultimaTentativa: string | null;
  reprocessable: boolean;
}

const parseBrlAmount = (raw: string): number | null => {
  const n = parseFloat(raw.replace(',', '.').replace(/[^\d.-]/g, ''));
  return Number.isFinite(n) ? n : null;
};

// Mapeia um warning do backend em uma row com motivo renderizado (JSX) e texto.
const buildDivergenceRow = (w: ReconciliationWarning): DivergenceRow | null => {
  const txPk = w.transaction_pk;
  if (txPk == null) return null;

  let valueBrl: number | null = null;
  let motivo: React.ReactNode;
  let motivoText: string;

  if (w.type === 'PAGBANK_RECEIPT_MISMATCH') {
    // MV2-1901 review A1: preferir w.fields (projeção estruturada do backend) sobre regex.
    // Fallback pra regex em w.detail quando backend antigo não envia fields.
    const fmt = (n: unknown): string | null =>
      typeof n === 'number' ? n.toFixed(2) : (typeof n === 'string' ? n : null);
    const sentStr = fmt(w.fields?.sent);
    const receivedStr = fmt(w.fields?.received);
    const refundedStr = fmt(w.fields?.refunded);
    const grossStr = fmt(w.fields?.gross);

    const sentMatch = sentStr ?? (w.detail?.match(/sent=([^\s]+)/)?.[1] ?? null);
    const receivedMatch = receivedStr ?? (w.detail?.match(/received=([^\s]+)/)?.[1] ?? null);
    const refundedMatch = refundedStr ?? (w.detail?.match(/refunded=([^\s]+)/)?.[1] ?? null);
    const grossMatch = grossStr ?? (w.detail?.match(/gross=([^\s]+)/)?.[1] ?? null);

    if (sentMatch && receivedMatch) {
      // gross = valor bruto da venda (c.paymentAmount), antes de aplicar split.
      // sent = valor com split aplicado (bruto × percentual).
      // Mostramos o bruto na coluna "Valor R$" porque é o que o operador reconhece
      // da venda; sent/received ficam no texto do motivo.
      valueBrl = grossMatch ? parseBrlAmount(grossMatch) : parseBrlAmount(sentMatch);
      motivoText = `Esperado R$ ${sentMatch} × Recebido R$ ${receivedMatch}`
        + (refundedMatch ? ` · Estornado R$ ${refundedMatch}` : '');
      motivo = (
        <span>
          Esperado <strong>R$ {sentMatch}</strong> × Recebido{' '}
          <strong style={{ color: '#ff4d4f' }}>R$ {receivedMatch}</strong>
          {refundedMatch && (
            <>
              {' '}· Estornado{' '}
              <strong style={{ color: '#faad14' }}>R$ {refundedMatch}</strong>
            </>
          )}
        </span>
      );
    } else {
      motivoText = w.detail || 'Divergência de valores PagBank';
      motivo = <span>{motivoText}</span>;
    }
  } else if (w.type === 'UNRECONCILED_TX' || w.type === 'MISSING_PAGBANK_SPLIT' || w.type === 'MISSING_PAGBANK_TRANSFER') {
    motivoText = 'Não encontrada no PagBank';
    motivo = <span style={{ color: '#fa8c16' }}>Não encontrada no PagBank</span>;
  } else if (w.type === 'UNRECONCILED_ADJACENT_MONTH') {
    // Virada de mês: PagBank tem o split/transfer, mas o CSV atual não.
    // Reprocessar consulta o pagbank-ms direto e resolve sem precisar de novo upload.
    motivoText = 'Pode estar no CSV do mês anterior/seguinte — clique em Reprocessar';
    motivo = (
      <span style={{ color: '#fa8c16' }}>
        Pode estar no CSV do mês anterior/seguinte — clique em <strong>Reprocessar</strong>
      </span>
    );
  } else if (w.type === 'AMBIGUOUS_TX') {
    motivoText = 'Código ambíguo (múltiplos matches)';
    motivo = <span style={{ color: '#fa541c' }}>Código ambíguo (múltiplos matches)</span>;
  } else if (w.type === 'INVALID_PAYMENT_EXTERNAL_ID') {
    motivoText = 'ID PagBank inválido';
    motivo = <span style={{ color: '#fa541c' }}>ID PagBank inválido</span>;
  } else {
    motivoText = w.detail || w.type;
    motivo = <span>{motivoText}</span>;
  }

  // MV2-1908 reversão: reprocess é só re-consulta ao PagBank (não cria split/transfer),
  // então pode reavaliar qualquer status pendente, incluindo DIVERGENCE/AMBIGUOUS.
  const reprocessable =
    w.type === 'UNRECONCILED_TX'
    || w.type === 'UNRECONCILED_ADJACENT_MONTH'
    || w.type === 'MISSING_PAGBANK_SPLIT'
    || w.type === 'MISSING_PAGBANK_TRANSFER'
    || w.type === 'PAGBANK_RECEIPT_MISMATCH'
    || w.type === 'AMBIGUOUS_TX'
    || w.type === 'DUPLICATE_CODE';

  return {
    key: `${w.type}-${txPk}`,
    transactionPk: txPk,
    valueBrl,
    motivo,
    motivoText,
    ultimaTentativa: null,
    reprocessable,
  };
};

// Agrupa warnings por transaction_pk → uma linha por tx (concatena motivos quando múltiplos).
const buildDivergenceRows = (warnings: ReconciliationWarning[]): DivergenceRow[] => {
  const byTx = new Map<number, DivergenceRow>();
  for (const w of warnings) {
    const row = buildDivergenceRow(w);
    if (!row) continue;
    const existing = byTx.get(row.transactionPk);
    if (!existing) {
      byTx.set(row.transactionPk, row);
    } else if (row.reprocessable && !existing.reprocessable) {
      // Preserva o row mais "acionável" (reprocessável sobrepõe).
      byTx.set(row.transactionPk, row);
    }
  }
  return Array.from(byTx.values()).sort((a, b) => a.transactionPk - b.transactionPk);
};

interface BuildColumnsArgs {
  reprocessed: Set<number>;
  manuallyResolved: Set<number>;
  reprocessing: Set<number | 'all'>;
  manualDetailsByPk: Record<number, { userName: string; reason: string; at: string }>;
  onReprocess: (txPk: number) => void;
  onManual: (txPk: number) => void;
  onUndoManual: (txPk: number) => void;
  onOpenDetail: (txPk: number) => void;
  warningsByPk: Record<number, ReconciliationWarning>;
}

const buildDivergenceColumns = (args: BuildColumnsArgs) => [
  {
    title: 'Transação',
    dataIndex: 'transactionPk',
    key: 'transactionPk',
    width: 110,
    render: (pk: number) => (
      <a
        style={{ fontFamily: 'monospace' }}
        onClick={(e) => {
          e.preventDefault();
          args.onOpenDetail(pk);
        }}
      >
        {pk}
      </a>
    ),
  },
  {
    title: 'Valor R$',
    dataIndex: 'valueBrl',
    key: 'valueBrl',
    width: 100,
    render: (v: number | null) => v != null ? v.toFixed(2).replace('.', ',') : '—',
  },
  {
    title: 'Motivo',
    dataIndex: 'motivo',
    key: 'motivo',
    render: (motivo: React.ReactNode, row: DivergenceRow) => {
      const manual = args.manualDetailsByPk[row.transactionPk];
      if (manual) {
        return (
          <span style={{ color: 'rgba(0,0,0,.65)' }}>
            Conciliado manualmente — <strong>{manual.userName}</strong>
            {manual.reason && (
              <span style={{ color: 'rgba(0,0,0,.45)' }}> · &quot;{manual.reason}&quot;</span>
            )}
          </span>
        );
      }
      if (args.reprocessed.has(row.transactionPk)) {
        return <span style={{ color: '#52c41a' }}>Conciliada após reprocessamento</span>;
      }
      return motivo;
    },
  },
  {
    title: 'Ações',
    key: 'actions',
    width: 240,
    align: 'right' as const,
    render: (_: unknown, row: DivergenceRow) => {
      const isManual = !!args.manualDetailsByPk[row.transactionPk];
      const isReprocessed = args.reprocessed.has(row.transactionPk);
      if (isManual) {
        return (
          <Button size='small' type='link' onClick={() => args.onUndoManual(row.transactionPk)}>
            desfazer
          </Button>
        );
      }
      if (isReprocessed) {
        return <span style={{ color: 'rgba(0,0,0,.25)' }}>—</span>;
      }
      return (
        <span style={{ display: 'inline-flex', gap: 4 }}>
          {row.reprocessable && (
            <Button
              size='small'
              type='link'
              loading={args.reprocessing.has(row.transactionPk)}
              onClick={() => args.onReprocess(row.transactionPk)}
            >
              Reprocessar
            </Button>
          )}
          <Button size='small' type='link' onClick={() => args.onManual(row.transactionPk)}>
            Conciliar manualmente
          </Button>
        </span>
      );
    },
  },
];

// MV2-1909: tabela "Todas as recargas" no modo view — 1 linha por ReconciliationDetail.
const buildAllTxColumns = (args: { onOpenDetail: (txPk: number) => void }) => [
  {
    title: 'Transação',
    dataIndex: 'transaction_pk',
    key: 'transaction_pk',
    width: 110,
    render: (pk: number) => (
      <a
        style={{ fontFamily: 'monospace' }}
        onClick={(e) => { e.preventDefault(); args.onOpenDetail(pk); }}
      >
        {pk}
      </a>
    ),
  },
  {
    title: 'Cliente',
    dataIndex: 'customer_name',
    key: 'customer_name',
    render: (v: string | null) => v || <span style={{ color: 'rgba(0,0,0,.25)' }}>—</span>,
  },
  {
    title: 'Forma',
    dataIndex: 'payment_type',
    key: 'payment_type',
    width: 130,
    render: (pt: string | null) => {
      if (!pt) return <span style={{ color: 'rgba(0,0,0,.25)' }}>—</span>;
      if (pt === 'PAGBANK_CARD') return 'Cartão PagBank';
      if (pt === 'PAGBANK_PIX') return 'PIX PagBank';
      if (pt === 'WALLET') return 'Carteira';
      return pt;
    },
  },
  {
    title: 'Valor R$',
    dataIndex: 'value_brl',
    key: 'value_brl',
    width: 110,
    align: 'right' as const,
    render: (v: number | null) => v != null
      ? v.toFixed(2).replace('.', ',')
      : <span style={{ color: 'rgba(0,0,0,.25)' }}>—</span>,
  },
  {
    title: 'Recebido R$',
    dataIndex: 'found_amount',
    key: 'found_amount',
    width: 120,
    align: 'right' as const,
    render: (v: string | null) => {
      if (v == null) return <span style={{ color: 'rgba(0,0,0,.25)' }}>—</span>;
      const n = Number(v);
      return Number.isFinite(n)
        ? n.toFixed(2).replace('.', ',')
        : <span style={{ color: 'rgba(0,0,0,.25)' }}>—</span>;
    },
  },
  {
    title: 'Status',
    dataIndex: 'effective_status',
    key: 'effective_status',
    width: 160,
    render: (s: string | null) => {
      const meta = s ? RECONCILIATION_STATUS_META[s] : null;
      return meta
        ? <Tag color={meta.color}>{meta.label}</Tag>
        : <span style={{ color: 'rgba(0,0,0,.25)' }}>{s || '—'}</span>;
    },
  },
];

// antd 4.10 beforeUpload must return boolean | Promise. We always return false
// (never auto-upload); validation errors are surfaced via message + rejecting the
// file in onChange by filtering it out of fileList.
const validateFile = (file: RcFile): boolean => {
  if (!isAcceptedFormat(file)) {
    message.error('Apenas arquivos .csv ou .xlsx são aceitos.');
    return false;
  }
  if (file.size / 1024 / 1024 > MAX_SIZE_MB) {
    message.error(`Arquivo acima do limite de ${MAX_SIZE_MB} MB.`);
    return false;
  }
  return true;
};

/**
 * MV2-1909: converte XLSX → CSV client-side (SheetJS). CSV mantém-se como está.
 * Backend ocpp aceita só CSV; usamos o parse local pra que o operador não precise
 * fazer File→Save As no Excel toda vez.
 */
/**
 * MV2-1909: detecta pelo header se é "Extrato da Conta - PagSeguro" ou
 * "Relatório O que vendi - PagBank". Serve pra impedir upload invertido ou
 * dois arquivos do mesmo tipo nos dois campos.
 */
type CsvKind = 'extrato' | 'vendas' | 'unknown';

const detectCsvKind = async (file: File): Promise<CsvKind> => {
  const sample = (await file.slice(0, 2048).text()).trim();
  const header = sample.split(/\r?\n/)[0] || '';
  const lower = header.toLowerCase().replace(/\uFEFF/g, '').trim();
  // Extrato: começa com "CODIGO DA TRANSACAO" (sem acentos) e usa ; separator
  if (lower.startsWith('codigo da transacao')) return 'extrato';
  // Vendas: começa com "Documento;Estabelecimento;..."
  if (lower.startsWith('documento;estabelecimento')) return 'vendas';
  return 'unknown';
};

const convertToCsvIfNeeded = async (file: File): Promise<File> => {
  if (file.name.toLowerCase().endsWith('.csv')) return file;

  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const firstSheetName = workbook.SheetNames[0];
  if (!firstSheetName) {
    throw new Error('Planilha XLSX vazia — nenhuma aba encontrada.');
  }
  const sheet = workbook.Sheets[firstSheetName];
  // FS=',' pra manter compatibilidade com o parser do backend (que detecta
  // vírgula). Vendas e extrato PagBank usam esse separador.
  const csvText = XLSX.utils.sheet_to_csv(sheet, { FS: ',' });
  const bom = '\uFEFF'; // ajuda operadores que abrem o CSV manualmente no Excel
  const csvBlob = new Blob([bom + csvText], { type: 'text/csv;charset=utf-8' });
  const newName = file.name.replace(/\.xlsx$/i, '.csv');
  return new File([csvBlob], newName, { type: 'text/csv' });
};

const ReconciliationUploadModal: React.FC<Props> = ({
  visible,
  onClose,
  onDone,
  viewPeriod,
}) => {
  const isViewMode = !!viewPeriod;
  const [period, setPeriod] = useState<Moment | null>(null);
  const [extratoFile, setExtratoFile] = useState<UploadFile | null>(null);
  const [vendasFile, setVendasFile] = useState<UploadFile | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ReconciliationUploadResponse | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  // tx_pks que foram conciliados manualmente após o upload — removemos da lista
  // de alertas e incrementamos o contador "Conciliadas" do resumo.
  const [manuallyResolved, setManuallyResolved] = useState<Set<number>>(
    new Set(),
  );
  const [manualTarget, setManualTarget] = useState<ManualTargetRow | null>(null);
  const [manualMode, setManualMode] = useState<ManualMode>('reconcile');
  // MV2-1908: tx_pks que foram reprocessados com sucesso (UNRECONCILED → RECONCILED).
  // Removemos da lista de alertas e incrementamos "Conciliadas" no resumo.
  const [reprocessed, setReprocessed] = useState<Set<number>>(new Set());
  // Loader local pros botões "Reprocessar" (unitário ou lote)
  const [reprocessing, setReprocessing] = useState<Set<number | 'all'>>(new Set());
  // MV2-1909: contexto da conciliação manual pra renderizar inline na tabela
  // (user, motivo, data). Chave = transaction_pk.
  const [manualDetailsByPk, setManualDetailsByPk] = useState<Record<number, {
    userName: string;
    reason: string;
    at: string;
  }>>({});
  // MV2-1909: modal "Detalhe da conciliação" (mockup 5) aberto ao clicar numa linha.
  // Em modo view usamos ReconciliationDetail completo (do campo details); no modal pós-upload,
  // montamos um detail parcial a partir do warning disponível.
  const [detailTx, setDetailTx] = useState<ReconciliationDetail | null>(null);
  // MV2-1909: busca por tx_pk / cliente na tabela "Todas as recargas" (modo view).
  const [allTxSearch, setAllTxSearch] = useState('');

  const [refreshingView, setRefreshingView] = useState(false);

  /**
   * MV2-1909: re-fetcha o estado atual da competência em modo view. Chamado:
   *  - no mount (quando o modal abre em modo view)
   *  - após reprocess (unitário ou lote) pra operador ver o novo contador e status
   *  - pelo botão explícito "Atualizar"
   */
  const refreshView = async () => {
    if (!isViewMode || !viewPeriod) return;
    setRefreshingView(true);
    try {
      const resp = await getReconciliationView(viewPeriod);
      setResult(resp);
      // Depois do refresh, as pks que estavam no set manual/reprocess já foram
      // consolidadas no backend. Limpa os sets locais pra evitar dupla contagem.
      setManuallyResolved(new Set());
      setReprocessed(new Set());
      setManualDetailsByPk({});
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Falha ao atualizar competência.');
    } finally {
      setRefreshingView(false);
    }
  };

  // MV2-1909: modo view (vindo da tela Histórico) — carrega o estado atual sem exigir upload.
  useEffect(() => {
    if (!visible || !isViewMode || !viewPeriod) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await getReconciliationView(viewPeriod);
        if (!cancelled) setResult(resp);
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.response?.data?.message || 'Falha ao carregar competência.');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [visible, isViewMode, viewPeriod]);

  const reset = () => {
    setPeriod(null);
    setExtratoFile(null);
    setVendasFile(null);
    setResult(null);
    setError(null);
    setSubmitting(false);
    setManuallyResolved(new Set());
    setReprocessed(new Set());
    setReprocessing(new Set());
    setManualDetailsByPk({});
    setDetailTx(null);
    setAllTxSearch('');
    setManualTarget(null);
  };

  const handleClose = () => {
    // Modo view (vindo do Histórico) nunca dispara onDone — não houve upload novo.
    const shouldFireDone = !!result && !isViewMode;
    reset();
    onClose();
    if (shouldFireDone && onDone) onDone();
  };

  const canSubmit =
    !!period && !!extratoFile && !!vendasFile && !submitting && !result;

  const handleSubmit = async () => {
    if (!period || !extratoFile || !vendasFile) {
      setError('Envie os dois arquivos (extrato PagSeguro + relatório de vendas) antes de processar.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const periodStr = period.format('YYYY-MM');
      const extratoRaw = await convertToCsvIfNeeded(
        (extratoFile.originFileObj || extratoFile) as File,
      );
      const vendasRaw = await convertToCsvIfNeeded(
        (vendasFile.originFileObj || vendasFile) as File,
      );

      // MV2-1909: validação pré-upload — detecta inversão ou arquivos iguais.
      const kExtrato = await detectCsvKind(extratoRaw);
      const kVendas = await detectCsvKind(vendasRaw);
      if (kExtrato === 'vendas' && kVendas === 'extrato') {
        throw new Error(
          'Parece que você inverteu os arquivos. '
          + 'O "Extrato da Conta - PagSeguro" vai no campo Extrato e o "Relatório O que vendi - PagBank" no campo Vendas.',
        );
      }
      // Ordem importa: casos "ambos iguais" ANTES de "um no campo errado",
      // senão o `||` abaixo elimina 'extrato' do type de kVendas (TS narrow),
      // invalidando os checks de pares.
      if (kExtrato === 'extrato' && kVendas === 'extrato') {
        throw new Error('Os dois arquivos são do tipo Extrato. Envie o relatório de vendas no campo correspondente.');
      }
      if (kExtrato === 'vendas' && kVendas === 'vendas') {
        throw new Error('Os dois arquivos são do tipo Relatório de Vendas. Envie o extrato da conta no campo correspondente.');
      }
      if (kExtrato === 'vendas' || kVendas === 'extrato') {
        throw new Error(
          `Arquivo no campo errado: ${kExtrato === 'vendas' ? 'o Extrato recebeu um relatório de vendas' : 'o campo Vendas recebeu um extrato'}. Verifique antes de reenviar.`,
        );
      }
      if (kExtrato === 'unknown' || kVendas === 'unknown') {
        throw new Error(
          'Não reconhecemos o cabeçalho de um dos arquivos. '
          + 'Confira se exportou pelo PagSeguro/PagBank sem renomear as colunas.',
        );
      }

      const response = await uploadReconciliation(
        extratoRaw,
        vendasRaw,
        periodStr,
      );
      setResult(response);
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        'Falha inesperada ao processar a conciliação.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // MV2-1908: warning types elegíveis pra reprocessamento = tudo que não está
  // conciliado. Reprocess é só re-consulta ao PagBank (não cria split/transfer),
  // então pode reavaliar qualquer status pendente, incluindo DIVERGENCE/AMBIGUOUS.
  const REPROCESSABLE_WARNING_TYPES = new Set<string>([
    'UNRECONCILED_TX',
    'UNRECONCILED_ADJACENT_MONTH',
    'MISSING_PAGBANK_SPLIT',
    'MISSING_PAGBANK_TRANSFER',
    'PAGBANK_RECEIPT_MISMATCH',
    'AMBIGUOUS_TX',
    'DUPLICATE_CODE',
  ]);

  const isReprocessable = (w: ReconciliationWarning): boolean => {
    return REPROCESSABLE_WARNING_TYPES.has(w.type) && w.transaction_pk != null;
  };

  const handleReprocess = async (txPk: number) => {
    if (!result) return;
    setReprocessing(prev => new Set(prev).add(txPk));
    try {
      const res = await reprocessReconciliation(txPk, result.reference_period);
      if (res.changed) {
        setReprocessed(prev => new Set(prev).add(txPk));
        message.success(`tx ${txPk} reconciliada`);
      } else {
        // Mensagem contextual: distingue DIVERGENCE que continua divergente
        // de UNRECONCILED/AMBIGUOUS que continuam sem match no PagBank.
        const status = res.newAutoStatus;
        if (status === 'DIVERGENCE') {
          message.info(`tx ${txPk} continua divergente — valor recebido no PagBank ainda não bate com o esperado`);
        } else if (status === 'AMBIGUOUS') {
          message.info(`tx ${txPk} continua ambígua — múltiplos matches no PagBank`);
        } else {
          message.info(`tx ${txPk} ainda sem split/transfer no PagBank`);
        }
      }
      // Em modo view, re-fetcha pra refletir o estado atual (totals + details + warnings).
      if (isViewMode && res.changed) await refreshView();
    } catch (e: any) {
      message.error(
        e?.response?.data?.message || `Falha ao reprocessar tx ${txPk}`,
      );
    } finally {
      setReprocessing(prev => {
        const next = new Set(prev);
        next.delete(txPk);
        return next;
      });
    }
  };

  const handleReprocessAll = async () => {
    if (!result) return;
    const pending = result.warnings
      .filter(w => isReprocessable(w) && w.transaction_pk != null
        && !reprocessed.has(w.transaction_pk)
        && !manuallyResolved.has(w.transaction_pk))
      .map(w => w.transaction_pk as number);
    if (pending.length === 0) return;
    setReprocessing(prev => new Set(prev).add('all'));
    try {
      const res = await reprocessReconciliationBatch(pending, result.reference_period);
      if (res.nowReconciled > 0) {
        // Refaz a consulta das pks de novo pra marcar as que realmente mudaram.
        // Sem endpoint que retorna o detalhado por tx, assumimos: as primeiras
        // nowReconciled do pending viraram RECONCILED (ordem preservada no backend).
        // Em caso de divergência, user ainda vê as pendentes depois do refresh.
        setReprocessed(prev => {
          const next = new Set(prev);
          pending.slice(0, res.nowReconciled).forEach(pk => next.add(pk));
          return next;
        });
      }
      message.success(
        `${res.nowReconciled} reconciliada(s), ${res.stillUnreconciled} ainda pendente(s)`
          + (res.skipped > 0 ? `, ${res.skipped} pulada(s)` : ''),
      );
      if (isViewMode && res.nowReconciled > 0) await refreshView();
    } catch (e: any) {
      message.error(
        e?.response?.data?.message || 'Falha ao reprocessar em lote',
      );
    } finally {
      setReprocessing(prev => {
        const next = new Set(prev);
        next.delete('all');
        return next;
      });
    }
  };

  /**
   * MV2-1909: abre o modal de detalhe pra uma tx. Em modo view usamos o detail
   * completo (result.details[]); fora dele, montamos um detail parcial a partir
   * do warning disponível (sem cliente/datas/conferência completa).
   */
  const openDetailByTxPk = (txPk: number) => {
    if (!result) return;
    const fromDetails = (result.details || []).find(d => d.transaction_pk === txPk);
    if (fromDetails) {
      setDetailTx(fromDetails);
      return;
    }
    // Fallback: constrói parcial a partir do warning (sem dados completos).
    const w = (result.warnings || []).find(wn => wn.transaction_pk === txPk);
    if (!w) return;
    let expected: string | null = null;
    let found: string | null = null;
    let refunded: number | null = null;
    if (w.type === 'PAGBANK_RECEIPT_MISMATCH') {
      // MV2-1901 review A1: prefer structured w.fields; fall back to regex over w.detail.
      const toStr = (n: unknown): string | null =>
        typeof n === 'number' ? n.toFixed(2) : (typeof n === 'string' ? n : null);
      expected = toStr(w.fields?.sent) ?? (w.detail?.match(/sent=([^\s]+)/)?.[1]?.replace(',', '.') ?? null);
      found = toStr(w.fields?.received) ?? (w.detail?.match(/received=([^\s]+)/)?.[1]?.replace(',', '.') ?? null);
      const refundedField = w.fields?.refunded;
      if (typeof refundedField === 'number') refunded = refundedField;
      else {
        const m = w.detail?.match(/refunded=([^\s]+)/);
        if (m) refunded = parseFloat(m[1].replace(',', '.'));
      }
    }
    const autoStatus = w.type === 'PAGBANK_RECEIPT_MISMATCH' ? 'DIVERGENCE'
      : w.type === 'AMBIGUOUS_TX' ? 'AMBIGUOUS'
      : 'UNRECONCILED';
    setDetailTx({
      transaction_pk: txPk,
      customer_name: null,
      payment_type: null,
      payment_external_id: null,
      start_timestamp: null,
      paid_timestamp: null,
      value_brl: null,
      refunded_brl: refunded,
      split_percentage: null,
      expected_amount: expected,
      found_amount: found,
      auto_status: autoStatus,
      effective_status: autoStatus,
      auto_reconciled_at: null,
      manual_reason: null,
      manual_by_user_name: null,
      manual_at: null,
    });
  };

  const openUndoFromRow = (txPk: number) => {
    if (!result) return;
    setManualMode('undo');
    setManualTarget({
      transactionPk: txPk,
      referencePeriod: result.reference_period,
      autoStatus: null,
      effectiveStatus: 'RECONCILED_MANUAL',
    });
  };

  const openManualFromWarning = (txPk: number) => {
    if (!result) return;
    // Mapeia tipo de warning → autoStatus pra o modal exibir o contexto certo.
    // Melhor que inferir — o backend já colocou a tx numa dessas categorias.
    setManualMode('reconcile');
    setManualTarget({
      transactionPk: txPk,
      referencePeriod: result.reference_period,
      autoStatus: null, // modal mostra "—" nesse campo; info já está no título
      effectiveStatus: null,
    });
  };

  // renderWarning/warningTxPks removidos — a lista virou <Table> construída pelos helpers
  // top-level buildDivergenceRows + buildDivergenceColumns.

  return (
    <Modal
      title={isViewMode
        ? `Conciliação de ${viewPeriod} — detalhes`
        : 'Conciliar pagamentos PagBank'}
      visible={visible}
      width={960}
      maskClosable={false}
      destroyOnClose
      bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      onCancel={handleClose}
      footer={
        result ? (
          <>
            <Button
              onClick={async () => {
                try {
                  await downloadReconciliationReport(
                    result.reference_period,
                    'xlsx',
                  );
                } catch (e: any) {
                  message.error(
                    e?.response?.data?.message ||
                      'Falha ao baixar XLSX. Tente novamente.',
                  );
                }
              }}
            >
              Baixar XLSX
            </Button>
            <Button
              onClick={async () => {
                try {
                  await downloadReconciliationReport(
                    result.reference_period,
                    'pdf',
                  );
                } catch (e: any) {
                  message.error(
                    e?.response?.data?.message ||
                      'Falha ao baixar PDF. Tente novamente.',
                  );
                }
              }}
            >
              Baixar PDF
            </Button>
            <Button type="primary" onClick={handleClose}>
              {isViewMode ? 'Fechar' : 'Fechar e atualizar listagem'}
            </Button>
          </>
        ) : isViewMode ? (
          <Button onClick={handleClose}>Fechar</Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={submitting}>
              Cancelar
            </Button>
            <Button
              type="primary"
              onClick={handleSubmit}
              disabled={!canSubmit}
              loading={submitting}
            >
              Processar
            </Button>
          </>
        )
      }
    >
      {!result && !isViewMode && (
        <Form layout="vertical">
          <Form.Item
            label="Competência"
            required
            help="Mês e ano dos arquivos importados."
          >
            <DatePicker
              picker="month"
              value={period}
              onChange={setPeriod}
              format="MM/YYYY"
              placeholder="MM/AAAA"
              disabled={submitting}
              style={{ width: '100%' }}
              disabledDate={d => d && d.isAfter(moment().endOf('month'))}
            />
          </Form.Item>

          <SectionTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span>Extrato conta corrente (.csv)</span>
            <HelpLink type='extrato' />
          </SectionTitle>
          <Dragger
            name="extrato"
            multiple={false}
            maxCount={1}
            accept=".csv,.xlsx"
            beforeUpload={() => false}
            fileList={extratoFile ? [extratoFile] : []}
            onChange={({ fileList }) => {
              const f = fileList[fileList.length - 1];
              if (!f) {
                setExtratoFile(null);
                return;
              }
              const rc = (f.originFileObj || f) as RcFile;
              if (!validateFile(rc)) return;
              setExtratoFile(f);
            }}
            onRemove={() => {
              setExtratoFile(null);
              return true;
            }}
            disabled={submitting}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Arraste o extrato aqui ou clique pra selecionar
            </p>
            <p className="ant-upload-hint">
              Formatos aceitos: .xlsx ou .csv (até {MAX_SIZE_MB} MB)
            </p>
          </Dragger>

          <SectionTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span>Relatório de vendas (.csv)</span>
            <HelpLink type='vendas' />
          </SectionTitle>
          <Dragger
            name="vendas"
            multiple={false}
            maxCount={1}
            accept=".csv,.xlsx"
            beforeUpload={() => false}
            fileList={vendasFile ? [vendasFile] : []}
            onChange={({ fileList }) => {
              const f = fileList[fileList.length - 1];
              if (!f) {
                setVendasFile(null);
                return;
              }
              const rc = (f.originFileObj || f) as RcFile;
              if (!validateFile(rc)) return;
              setVendasFile(f);
            }}
            onRemove={() => {
              setVendasFile(null);
              return true;
            }}
            disabled={submitting}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Arraste o relatório aqui ou clique pra selecionar
            </p>
            <p className="ant-upload-hint">
              Formatos aceitos: .xlsx ou .csv (até {MAX_SIZE_MB} MB)
            </p>
          </Dragger>

          {error && (
            <Alert
              type="error"
              message="Falha ao processar"
              description={error}
              showIcon
              style={{ marginTop: 16 }}
            />
          )}
        </Form>
      )}

      {result && (() => {
        // Resumo "ao vivo": subtrai do bucket pendente e soma em conciliadas conforme
        // o operador resolve manualmente na lista de alertas abaixo. Heurística: toda
        // tx resolvida reduz 1 de divergence; se divergence zerou, cai pra unreconciled;
        // se zerou também, cai pra ambiguous. Não é perfeito (não sabemos de qual bucket
        // a tx veio) mas serve pro operador ver progresso durante a sessão.
        // MV2-1908: manual + reprocess resolvem linhas sem precisar refazer upload.
        // Soma os dois sets pra abater do div/unr/amb e somar em reconciled.
        const resolvedCount = manuallyResolved.size + reprocessed.size;
        let div = result.totals.divergence;
        let unr = result.totals.unreconciled;
        let amb = result.totals.ambiguous;
        // Reprocess só opera em UNRECONCILED → abate dessa fatia primeiro.
        // Manual abate indiscriminado → consome o restante.
        let remainingReprocess = reprocessed.size;
        let remaining = manuallyResolved.size;
        const takeReprocessFrom = (n: number) => {
          const taken = Math.min(remainingReprocess, n);
          remainingReprocess -= taken;
          return n - taken;
        };
        unr = takeReprocessFrom(unr);
        const takeFrom = (n: number) => {
          const taken = Math.min(remaining, n);
          remaining -= taken;
          return n - taken;
        };
        div = takeFrom(div);
        unr = takeFrom(unr);
        amb = takeFrom(amb);
        const reconciled = result.totals.reconciled + resolvedCount;

        return (
        <>
          <Alert
            type="success"
            icon={<CheckCircleOutlined />}
            showIcon
            message={`Conciliação da competência ${result.reference_period} concluída`}
          />
          <SectionTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Resultado da conciliação</span>
            {isViewMode && (
              <Tooltip title='Atualizar informações'>
                <Button
                  shape='circle'
                  size='small'
                  type='text'
                  icon={<ReloadOutlined spin={refreshingView} />}
                  onClick={refreshView}
                />
              </Tooltip>
            )}
          </SectionTitle>
          {(() => {
            // "Não encontradas" é guarda-chuva pro visual do modal:
            // tudo que não entrou automático como RECONCILED (operador precisa agir).
            const naoEncontradas = div + unr + amb;
            const wallet = result.totals.wallet ?? 0;
            const cardPix = result.totals.card_pix ?? 0;
            return (
              <Row gutter={16}>
                <Col span={6}>
                  <Statistic title="Total de recargas" value={result.totals.candidates} />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Conciliadas"
                    value={reconciled}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="Não encontradas"
                    value={naoEncontradas}
                    valueStyle={{ color: '#fa8c16' }}
                  />
                </Col>
                <Col span={6}>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontSize: 12, color: 'rgba(0,0,0,.45)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Carteira × Cartão/PIX
                    </div>
                    <div style={{ marginTop: 4 }}>
                      <span style={{ fontSize: 20, fontWeight: 500 }}>{wallet}</span>
                      <span style={{ fontSize: 12, color: 'rgba(0,0,0,.45)', marginLeft: 4 }}>carteira</span>
                      <span style={{ margin: '0 8px', color: 'rgba(0,0,0,.25)' }}>·</span>
                      <span style={{ fontSize: 20, fontWeight: 500 }}>{cardPix}</span>
                      <span style={{ fontSize: 12, color: 'rgba(0,0,0,.45)', marginLeft: 4 }}>cartão/pix</span>
                    </div>
                  </div>
                </Col>
              </Row>
            );
          })()}
          {(() => {
            // MV2-1909: lista de divergências agora é Table. Linhas = warnings do backend.
            // Linha "resolvida" (manual ou reprocess) permanece cinza mostrando o estado.
            const pendingReprocessable = (result.warnings || []).filter(w =>
              isReprocessable(w)
              && w.transaction_pk != null
              && !reprocessed.has(w.transaction_pk)
              && !manuallyResolved.has(w.transaction_pk)
            ).length;
            const rows = buildDivergenceRows(result.warnings || []);
            return (
              <>
                <SectionTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24 }}>
                  <span>Divergências ({rows.length})</span>
                  {pendingReprocessable > 0 && (
                    <Button
                      size='small'
                      type='primary'
                      ghost
                      loading={reprocessing.has('all')}
                      onClick={handleReprocessAll}
                    >
                      Reprocessar divergências ({pendingReprocessable})
                    </Button>
                  )}
                </SectionTitle>
                {rows.length === 0 ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '24px 16px',
                    color: 'rgba(0,0,0,.45)',
                    border: '1px dashed rgba(0,0,0,.15)',
                    borderRadius: 4,
                  }}>
                    Nenhuma divergência pendente — tudo reprocessado.
                  </div>
                ) : (
                  <Table
                    size='small'
                    dataSource={rows}
                    rowKey='key'
                    pagination={false}
                    columns={buildDivergenceColumns({
                      reprocessed,
                      manuallyResolved,
                      reprocessing,
                      manualDetailsByPk,
                      onReprocess: handleReprocess,
                      onManual: openManualFromWarning,
                      onUndoManual: (txPk) => openUndoFromRow(txPk),
                      onOpenDetail: (txPk) => openDetailByTxPk(txPk),
                      warningsByPk: (result.warnings || []).reduce((acc, wn) => {
                        if (wn.transaction_pk != null) acc[wn.transaction_pk] = wn;
                        return acc;
                      }, {} as Record<number, ReconciliationWarning>),
                    })}
                  />
                )}
              </>
            );
          })()}
          {/* MV2-1909: em modo view (vindo do Histórico), lista TODAS as tx pra abrir
              detalhe de qualquer uma — não só as pendentes. */}
          {isViewMode && result.details && result.details.length > 0 && (() => {
            const q = allTxSearch.trim().toLowerCase();
            const filtered = q
              ? result.details!.filter(d =>
                  String(d.transaction_pk).includes(q)
                  || (d.customer_name && d.customer_name.toLowerCase().includes(q))
                  || (d.payment_external_id && d.payment_external_id.toLowerCase().includes(q))
                )
              : result.details!;
            return (
              <>
                <SectionTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24 }}>
                  <span>
                    Todas as recargas ({filtered.length}
                    {q && filtered.length !== result.details!.length && ` de ${result.details!.length}`})
                  </span>
                  <Input
                    allowClear
                    size='small'
                    placeholder='Buscar por ID, cliente ou ID PagBank'
                    value={allTxSearch}
                    onChange={(e) => setAllTxSearch(e.target.value)}
                    style={{ width: 280 }}
                  />
                </SectionTitle>
                {filtered.length === 0 ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '24px 16px',
                    color: 'rgba(0,0,0,.45)',
                    border: '1px dashed rgba(0,0,0,.15)',
                    borderRadius: 4,
                  }}>
                    Nenhuma recarga encontrada para &quot;{allTxSearch}&quot;.
                  </div>
                ) : (
                  <Table
                    size='small'
                    dataSource={filtered}
                    rowKey='transaction_pk'
                    pagination={{ pageSize: 10, size: 'small' }}
                    columns={buildAllTxColumns({ onOpenDetail: openDetailByTxPk })}
                  />
                )}
              </>
            );
          })()}
        </>
        );
      })()}

      {/* Modal "Detalhe da conciliação" (mockup 5) — aberto ao clicar na linha */}
      {(() => {
        if (!detailTx) {
          return (
            <ReconciliationDetailModal
              visible={false}
              detail={null}
              onClose={() => setDetailTx(null)}
            />
          );
        }
        const txPk = detailTx.transaction_pk;
        // Reprocess é oferecido pra qualquer status pendente (não manual, não RECONCILED).
        const reprocessable = detailTx.effective_status !== 'RECONCILED_MANUAL'
          && detailTx.auto_status !== 'RECONCILED'
          && detailTx.auto_status != null;
        return (
          <ReconciliationDetailModal
            visible={true}
            detail={detailTx}
            onClose={() => setDetailTx(null)}
            isReprocessable={reprocessable}
            isReprocessing={reprocessing.has(txPk)}
            onReprocess={() => {
              handleReprocess(txPk);
              setDetailTx(null);
            }}
            onManual={() => {
              setDetailTx(null);
              openManualFromWarning(txPk);
            }}
          />
        );
      })()}

      {/* Modal manual aninhado — alvo escolhido pelo usuário via botão em cada alerta */}
      <ReconciliationManualModal
        visible={manualTarget !== null}
        mode={manualMode}
        target={manualTarget}
        onClose={() => setManualTarget(null)}
        onDone={(resp) => {
          if (!manualTarget) return;
          const txPk = manualTarget.transactionPk;
          const authUser = JSON.parse(localStorage.getItem('auth-user') || 'null');
          const userName = authUser
            ? [authUser.first_name, authUser.last_name].filter(Boolean).join(' ').trim()
              || authUser.name || authUser.email || `user #${resp.manual_reconciled_by_user_pk}`
            : `user #${resp.manual_reconciled_by_user_pk}`;

          if (manualMode === 'reconcile') {
            setManuallyResolved(prev => {
              const next = new Set(prev);
              next.add(txPk);
              return next;
            });
            setManualDetailsByPk(prev => ({
              ...prev,
              [txPk]: {
                userName,
                reason: resp.manual_reason || '',
                at: resp.manual_reconciled_at || new Date().toISOString(),
              },
            }));
          } else {
            // Undo: tira da lista de manual-resolved e do map de detalhes.
            setManuallyResolved(prev => {
              const next = new Set(prev);
              next.delete(txPk);
              return next;
            });
            setManualDetailsByPk(prev => {
              const next = { ...prev };
              delete next[txPk];
              return next;
            });
          }

          // Refresh in-memory da lista geral de recargas (view mode) sem round-trip:
          // atualiza effective_status + manual_* da linha afetada pra re-render automático.
          setResult(prev => {
            if (!prev || !prev.details) return prev;
            const details = prev.details.map(d => {
              if (d.transaction_pk !== txPk) return d;
              if (manualMode === 'reconcile') {
                return {
                  ...d,
                  effective_status: 'RECONCILED_MANUAL',
                  manual_reason: resp.manual_reason ?? null,
                  manual_at: resp.manual_reconciled_at ?? new Date().toISOString(),
                  manual_by_user_name: userName,
                };
              }
              return {
                ...d,
                effective_status: resp.auto_status ?? d.auto_status,
                manual_reason: null,
                manual_at: null,
                manual_by_user_name: null,
              };
            });
            return { ...prev, details };
          });
        }}
      />
    </Modal>
  );
};

export default ReconciliationUploadModal;
